/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  formatTimeHM_TZ,
  formatToDDMMYYYY,
  formatWeekdayDDMMYY,
  formatWeekdayDDMMYYYY,
} from "../date";
import { AgeData } from "../interfaces";
import type { ProfessionalDataResponse } from "@/entities/professional";

// Total de registros desde pages de useInfiniteQuery
export function getTotalRecordsFromPages(pages?: any[]): number {
  return pages?.[0]?.data?.pagination?.total ?? 0;
}

// Fecha de óbito desde la primer page (o null)
export function getDeceasedDateTimeFromPages(pages?: any[]): string | null {
  return pages?.[0]?.data?.deceasedDateTime ?? null;
}

// Id estable para cada sesión (evita repetir el template string)
export function formatSessionId(
  serviceNumber?: string | number | null,
  sessionNumber?: string | number | null,
): string {
  return [serviceNumber, sessionNumber]
    .filter((v) => v !== undefined && v !== null)
    .join("-");
}

// Formateo de fechas
export const formatDate = (
  date: string | Date,
  format:
    | "short"
    | "long"
    | "weekday"
    | "weekday2d"
    | "dayTime"
    | "time" = "short",
): string => {
  const timeZone = "America/Argentina/Buenos_Aires";

  // Normalizamos a string
  const input = typeof date === "string" ? date : (date?.toISOString?.() ?? "");

  switch (format) {
    case "weekday": {
      const text = formatWeekdayDDMMYYYY(input);
      return text || "Fecha inválida";
    }
    case "weekday2d": {
      const text = formatWeekdayDDMMYY(input, timeZone);
      return text || "Fecha inválida";
    }
    case "time": {
      const hhmm = formatTimeHM_TZ(input, timeZone);
      return hhmm || "Fecha inválida";
    }
    case "dayTime": {
      const ddmmyyyy = formatToDDMMYYYY(input);
      const hhmm = formatTimeHM_TZ(input, timeZone);
      if (!ddmmyyyy || ddmmyyyy === input) {
        // Si no pudimos formatear fecha, devolvemos “Fecha inválida” como antes
        return "Fecha inválida";
      }
      return `${ddmmyyyy} - ${hhmm}`;
    }
    case "long": {
      // Caso “largo” legacy: delegamos al locale con TZ fija (como tu versión)
      const d = new Date(input);
      if (Number.isNaN(d.getTime())) return "Fecha inválida";
      return d.toLocaleDateString("es-AR", {
        timeZone,
        year: "numeric",
        month: "long",
        day: "numeric",
        weekday: "long",
      });
    }
    case "short":
    default: {
      const ddmmyyyy = formatToDDMMYYYY(input);
      return ddmmyyyy && ddmmyyyy !== input ? ddmmyyyy : "Fecha inválida";
    }
  }
};

// Formateo de números
export const formatNumber = (
  number: number,
  options: {
    decimals?: number;
    currency?: boolean;
    percentage?: boolean;
    compact?: boolean;
  } = {},
): string => {
  const {
    decimals = 0,
    currency = false,
    percentage = false,
    compact = false,
  } = options;

  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  if (currency) {
    formatOptions.style = "currency";
    formatOptions.currency = "ARS";
  } else if (percentage) {
    formatOptions.style = "percent";
  }

  if (compact && Math.abs(number) >= 1000) {
    formatOptions.notation = "compact";
    formatOptions.compactDisplay = "short";
  }

  return number.toLocaleString("es-AR", formatOptions);
};

// Formateo de texto
export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const capitalizeWords = (text: string): string => {
  return text
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ");
};

export const truncateText = (
  text: string,
  maxLength: number,
  suffix = "...",
): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

// Formateo de documentos
export const formatDocumentNumber = (
  docNumber: string,
  docType: string = "DNI",
): string => {
  if (!docNumber) return "";
  // Remover caracteres no numéricos
  const cleanNumber = docNumber.replace(/\D/g, "");
  // Para DNI, retornar el número limpio sin formato
  if (docType.toUpperCase() === "DNI") {
    return cleanNumber;
  }

  return cleanNumber;
};

// Formateo de teléfonos
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return "";

  const cleanPhone = phone.replace(/\D/g, "");

  if (cleanPhone.length === 10) {
    // Formato: (XXX) XXX-XXXX
    return cleanPhone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  }

  if (cleanPhone.length === 11 && cleanPhone.startsWith("54")) {
    // Formato internacional: +54 (XXX) XXX-XXXX
    return cleanPhone.replace(/54(\d{3})(\d{3})(\d{4})/, "+54 ($1) $2-$3");
  }

  return phone;
};

// Validaciones
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidDNI = (dni: string): boolean => {
  const cleanDNI = dni.replace(/\D/g, "");
  return cleanDNI.length >= 7 && cleanDNI.length <= 8;
};

// Utilidades de tiempo
export const getTimeAgo = (input: string | Date): string => {
  const date = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return "Fecha inválida";

  const now = new Date();
  const diffMs = date.getTime() - now.getTime(); // >0 futuro, <0 pasado
  const diffSecAbs = Math.abs(diffMs) / 1000;

  // Menos de 60s: mensaje especial (compatibilidad con tests previos)
  if (diffSecAbs < 60) {
    return diffMs < 0 ? "Hace un momento" : "En un momento";
  }

  const r = (n: number) => Math.round(n);

  const inPast = diffMs < 0;
  const withLabel = (value: number, unitSing: string, unitPlur: string) => {
    const n = Math.abs(value);
    const label = n === 1 ? unitSing : unitPlur;
    return inPast ? `Hace ${n} ${label}` : `En ${n} ${label}`;
  };

  const minutes = diffSecAbs / 60;
  if (minutes < 60) return withLabel(r(minutes), "minuto", "minutos");

  const hours = minutes / 60;
  if (hours < 24) return withLabel(r(hours), "hora", "horas");

  const days = hours / 24;
  if (days < 30) return withLabel(r(days), "día", "días");

  // Aproximación mensual simple (30d) y anual (365d) para lectura humana
  const months = days / 30;
  if (months < 12) return withLabel(r(months), "mes", "meses");

  const years = days / 365;
  return withLabel(r(years), "año", "años");
};

// Utilidades de archivos
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Utilidades de colores
export const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Utilidades de arrays
export const groupBy = <T>(array: T[], key: keyof T): Record<string, T[]> => {
  return array.reduce(
    (groups, item) => {
      const group = String(item[key]);
      groups[group] = groups[group] || [];
      groups[group].push(item);
      return groups;
    },
    {} as Record<string, T[]>,
  );
};

export const sortBy = <T>(
  array: T[],
  key: keyof T,
  direction: "asc" | "desc" = "asc",
): T[] => {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  });
};

export const formatAge = (ageData?: AgeData): string => {
  if (!ageData) return "";

  const { years = 0, months = 0, days = 0 } = ageData;

  if (years > 0) {
    return `${years} año${years > 1 ? "s" : ""}`;
  }

  if (months > 0) {
    return `${months} mes${months > 1 ? "es" : ""}`;
  }

  if (days > 0) {
    return `${days} día${days > 1 ? "s" : ""}`;
  }

  return "Recién nacido";
};

// Formateo de profesionales
export const getProfessionalFullName = (
  data: ProfessionalDataResponse | undefined,
): string => {
  if (!data?.data?.professional_data_registered) {
    return "Usuario desconocido";
  }

  const { name, surname } = data.data.professional_data;

  // Validar que ambos campos existan y no estén vacíos
  if (!name || !surname) {
    return "Usuario desconocido";
  }

  // Retornar nombre completo
  return `${name} ${surname}`;
};

/**
 * Variante que retorna un objeto con más información del profesional
 */
export const getProfessionalInfo = (
  data: ProfessionalDataResponse | undefined,
) => {
  const professional = data?.data.professional_data_registered
    ? data.data.professional_data
    : null;
  const name = professional?.name ?? "";
  const surname = professional?.surname ?? "";

  return {
    fullName: getProfessionalFullName(data),
    firstName: name || "",
    lastName: surname || "",
    hasData: Boolean(name && surname),
  };
};
